import { useCallback, useState, type FormEvent, type JSX } from 'react';
import { useAuth } from './useAuth';

/**
 * Operator sign-in screen (RBAC R.2b, #314). The root gate shows this ONLY when the
 * server enforces auth (GET /api/auth/config → authEnforced) and no session is
 * resolved. On-brand with the COP (dark slate, full-screen centred card). The
 * failure message is generic — it never distinguishes unknown-email from
 * bad-password (no account enumeration), mirroring the server's single generic 401.
 */
export function LoginView(): JSX.Element {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const canSubmit = email.trim().length > 0 && password.length > 0 && !busy;

  const onSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      if (!canSubmit) return;
      setBusy(true);
      setFailed(false);
      const ok = await login(email.trim(), password).catch(() => false);
      if (!ok) {
        setFailed(true);
        setPassword('');
      }
      setBusy(false);
    },
    [canSubmit, login, email, password],
  );

  return (
    <div
      data-testid="login-view"
      className="flex h-full w-full items-center justify-center bg-slate-950 text-slate-200"
    >
      <form
        data-testid="login-form"
        onSubmit={(e) => void onSubmit(e)}
        className="w-80 space-y-4 rounded-lg border border-slate-700 bg-slate-900/95 p-6 shadow-2xl"
      >
        <div className="space-y-1">
          <h1 className="text-base font-semibold tracking-wide text-slate-100">
            TSR Operator Sign-in
          </h1>
          <p className="text-[11px] text-slate-500">Authorized operators only.</p>
        </div>

        <label className="block space-y-1">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Email
          </span>
          <input
            data-testid="login-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            className="h-8 w-full rounded border border-slate-600 bg-slate-900 px-2 text-xs text-slate-100 outline-none focus:border-sky-500 disabled:opacity-60"
          />
        </label>

        <label className="block space-y-1">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Password
          </span>
          <input
            data-testid="login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            className="h-8 w-full rounded border border-slate-600 bg-slate-900 px-2 text-xs text-slate-100 outline-none focus:border-sky-500 disabled:opacity-60"
          />
        </label>

        {failed && (
          <p data-testid="login-error" className="text-[11px] text-rose-400">
            Sign-in failed — check your email and password.
          </p>
        )}

        <button
          type="submit"
          data-testid="login-submit"
          disabled={!canSubmit}
          className="h-8 w-full rounded bg-sky-600 text-xs font-medium text-white hover:bg-sky-500 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-500"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
